import React, { useEffect, useId, useState } from "react";

type Exercise = { id: string; name: string };
type ExerciseProgress = {
  completed?: boolean;
  setsCompleted?: number;
  reps?: number;
  weight?: number;
  notes?: string;
};

export default function ExerciseLogger({
  exercise,
  value,
  onChange,
}: {
  exercise: Exercise;
  value: ExerciseProgress;
  onChange: (v: ExerciseProgress) => void;
}) {
  const [local, setLocal] = useState<ExerciseProgress>(value || {});
  const baseId = useId();
  const setsId = `${baseId}-sets`;
  const repsId = `${baseId}-reps`;
  const weightId = `${baseId}-weight`;
  const notesId = `${baseId}-notes`;
  useEffect(() => {
    // resync local edit buffer when the parent supplies a new `value`
    // (e.g. switching between exercises). Without this, stale local edits
    // would persist across exercises.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocal(value || {});
  }, [value]);
  function handleField<K extends keyof ExerciseProgress>(
    key: K,
    v: ExerciseProgress[K],
  ) {
    const nv = { ...local, [key]: v } as ExerciseProgress;
    setLocal(nv);
  }
  function save() {
    onChange(local);
  }
  return (
    <div style={styles.logger}>
      <div style={styles.loggerRow}>
        <span style={styles.label}>Exercise</span>
        <span style={styles.value}>{exercise.name}</span>
      </div>
      <div style={styles.loggerRow}>
        <label htmlFor={setsId} style={styles.label}>
          Sets
        </label>
        <input
          id={setsId}
          type="number"
          min={0}
          value={local.setsCompleted ?? 0}
          onChange={(e) => handleField("setsCompleted", Number(e.target.value))}
          style={styles.input}
        />
      </div>
      <div style={styles.loggerRow}>
        <label htmlFor={repsId} style={styles.label}>
          Reps
        </label>
        <input
          id={repsId}
          type="number"
          min={0}
          value={local.reps ?? 0}
          onChange={(e) => handleField("reps", Number(e.target.value))}
          style={styles.input}
        />
      </div>
      <div style={styles.loggerRow}>
        <label htmlFor={weightId} style={styles.label}>
          Weight
        </label>
        <input
          id={weightId}
          type="number"
          min={0}
          value={local.weight ?? 0}
          onChange={(e) => handleField("weight", Number(e.target.value))}
          style={styles.input}
        />
      </div>
      <div style={styles.loggerRow}>
        <label htmlFor={notesId} style={styles.label}>
          Notes
        </label>
        <input
          id={notesId}
          type="text"
          value={local.notes ?? ""}
          onChange={(e) => handleField("notes", e.target.value)}
          style={styles.input}
        />
      </div>
      <button onClick={save} style={styles.saveBtn}>
        Save
      </button>
    </div>
  );
}

const styles: { [k: string]: React.CSSProperties } = {
  logger: { backgroundColor: "#2b2b2b", padding: 12, borderRadius: 8 },
  loggerRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  label: { width: 70, fontSize: 12, color: "#ddd" },
  value: { fontSize: 13 },
  input: {
    flex: 1,
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid #555",
    backgroundColor: "#1a1a1a",
    color: "#fff",
  },
  saveBtn: {
    marginTop: 6,
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#ff6b35",
    color: "#fff",
  },
};
